import { useState } from "react";

import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import client from "../../../api/api";
import FormInputField from "../../../components/form-input-field";
import LoadingIndicator from "../../../components/loading-indicator";
import useTranslate, { useTranslatedStrings } from "../../../hooks/translate";
import { setCookie } from "../../../services/cookies";
import { setAuthToken } from "../../../slices/app-slice";
import {
  validatePassword,
  validateUsername,
} from "../../../utils/field-validation";
import "./login-form.css";
import SuccessDialog from "./success-dialog";

interface Props {
  onForgotClicked: () => void;
  onSignupClicked: () => void;
}

export default function LoginForm(props: Props) {
  const strings = useTranslatedStrings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useTranslate();

  const [isInitialState, setIsInitialState] = useState<boolean>(true);
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [recaptcha, setRecaptcha] = useState<string | null>(null);
  const [loading, setloading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  function isFormValid() {
    return (
      username &&
      validateUsername(username) &&
      password &&
      validatePassword(password)
    );
  }

  async function handleSubmitClicked() {
    if (!isFormValid()) {
      setIsInitialState(false);
      return;
    }

    if (username && password && recaptcha) {
      setloading(true);

      const response = await client.loginPost({
        username: username ?? "",
        password: password ?? "",
        recaptcha: recaptcha ?? "",
        source: "Wep App",
      });

      setloading(false);

      if (response.status === 200) {
        const { authToken } = response.data;
        client.setToken(authToken);
        setCookie("authToken", authToken ?? "");
        dispatch(setAuthToken(authToken));
        setSuccess(true);
      } else if (response.response.status === 503) {
        navigate("/service_unavailable");
      } else {
        const { data } = response.response;
        setError(data.errorDescriptionKey);
      }
    }

    setRefreshReCaptcha((r) => !r);
  }

  if (success) return <SuccessDialog message={strings.welcome} />;

  if (loading) return <LoadingIndicator />;

  return (
    <>
      <div className="login-dialog-title">{strings.welcome}</div>

      {error && <h6 className="error-message">{translate(error)}</h6>}

      <FormInputField
        isValid={isInitialState ? null : validateUsername(username)}
        type="username"
        placeholder={strings.username}
        errorMessage={strings.usernamereqs}
        onChange={(value) => {
          setIsInitialState(false);
          setUsername(value);
        }}
      />

      <FormInputField
        isValid={isInitialState ? null : validatePassword(password)}
        type="password"
        placeholder={strings.password}
        errorMessage={strings.passwordhelp}
        onChange={(value) => {
          setIsInitialState(false);
          setPassword(value);
        }}
      />

      <div className="forgot-password-text" onClick={props.onForgotClicked}>
        {strings.forgotpassword}
      </div>

      <GoogleReCaptcha
        onVerify={setRecaptcha}
        refreshReCaptcha={refreshReCaptcha}
      />

      <button
        className="btn smartnest-button"
        type="button"
        onClick={handleSubmitClicked}
      >
        {strings.login}
      </button>

      <div className="login-dialog-footer" onClick={props.onSignupClicked}>
        <div>{strings.notYetRegistered}</div>
        <div className="link">{strings.signup}</div>
      </div>
    </>
  );
}
